/**
 * The plan gate (owner, 2026-10-08: "lock out unpaid users and force subscription to get the free trial after signing in and
 * before inviting anyone"). One pure rule for the server's gate (services/api paywall.ts), the web and the phone.
 *
 *   level   what works
 *   full    everything (trialing, active; the sample café; homes; a server without the gate)
 *   grace   everything, with a "Fix your card" banner: a failed payment (past_due) for PLAN_GRACE_DAYS
 *   setup   a business that never had a card: setup, "Is this you?" and filling in every check, but a record waits for the
 *           card (owner, 2026-10-10: "After they put data in, to save it to the log"): its Save says "Almost there!" and keeps
 *           the entry as the person's own draft until the card is added (`holdsRecords`; services/api drafts.ts). No inviting,
 *           no second location and no bulk work (those need the plan from the start). Never a clock here.
 *   locked  after the grace, unpaid, canceled or never paid in time: reading what is there, the binder, the full export, the
 *           plan and the card, the cancel/erase clocks, removing people and signing out, and (never wall safety) health
 *           check-ins, sick reports, incident reports and their resolve.
 *
 * Homes keep their own rule (owner, 2026-10-09; parked): the first record opens FIRST_VALUE_HOURS, then the card (owner,
 * 2026-10-14: 3 free days, then $18 a month; core billing.ts HOME_TRIAL_DAYS, HOME_PLAN_PRICE_CENTS).
 *
 * Never held hostage (owner rule): a locked business still reads every record, downloads its binder and exports everything.
 */
import type { IsoDateTime, Organization } from "./model.ts";
import { isSampleOrgId } from "./buyintent.ts";
import { isEndedStatus } from "./subscription.ts";
import { TRIAL_DAYS, homeOfferLine, trialDaysFor } from "./billing.ts";

/** Days a failed payment keeps the app open, with the banner, before it locks. */
export const PLAN_GRACE_DAYS = 7;
/** Homes only: hours after a home's first record without a card that it may run (a business holds its records instead). */
export const FIRST_VALUE_HOURS = 8;
const DAY_MS = 86_400_000;

export type PlanLevel = "full" | "grace" | "setup" | "locked";
export type PlanGateReason =
  | "off" | "home" | "sample" | "trialing" | "active" | "past_due" | "grace_over" | "unpaid" | "canceled" | "incomplete_expired"
  | "incomplete" | "no_plan" | "first_value" | "test_account" | "county_not_open" | "prep";

export interface PlanAccess {
  level: PlanLevel;
  reason: PlanGateReason;
  /** Inviting, adding people, a second location, bulk work: only on a running plan (or its grace). */
  canGrow: boolean;
  /** past_due: when the grace ends (then it locks). */
  graceEndsAt?: IsoDateTime;
  /** A home in setup after its first record: when its window closes. */
  firstValueEndsAt?: IsoDateTime;
  /** A business with no card yet (setup, no_plan or incomplete): a record's Save is held as a draft until the card is added. */
  holdsRecords?: true;
}

export function planAccess(org: Pick<Organization, "id" | "kind" | "billing" | "firstValueAt"> & { testAccount?: boolean; prepOf?: Organization["prepOf"] }, now: Date, o: { enforced: boolean; /** No place in an open county (core waitlist.ts): Checkout waits, so nothing locks. */ held?: boolean; /** Homes pay (the server's HOMES_BILLING, default on there); absent here = homes stay open. */ homesBilling?: boolean }): PlanAccess {
  const full = (reason: PlanGateReason): PlanAccess => ({ level: "full", reason, canGrow: true });
  if (!o.enforced) return full("off");
  // Homes follow the same card rule as businesses when home billing is on (owner, 2026-10-09); paused, they stay open.
  // The sample café never reaches Stripe and never locks.
  if (org.kind === "home" && o.homesBilling !== true) return full("home");
  if (isSampleOrgId(org.id)) return full("sample");
  // A place a customer is setting up for its owner (prep.ts P7; audit F1): setup only, and every record waits for the card, exactly like
  // a business with no card. Never the Test flag: that is staff-set only.
  if (org.prepOf && !org.prepOf.closedAt && org.kind !== "home") return { level: "setup", reason: "prep", canGrow: false, holdsRecords: true };
  // A test account (owner, 2026-10-09: set by CleanCleaver staff in the team console, never by the customer) is never asked for a card.
  if (org.testAccount === true) return full("test_account");
  const b = org.billing;
  if (b?.status === "trialing") return full("trialing");
  if (b?.status === "active") return full("active");
  if (b?.status === "past_due") {
    const from = Date.parse(b.paymentFailedAt ?? b.statusAt ?? b.updatedAt);
    const ends = (Number.isFinite(from) ? from : now.getTime()) + PLAN_GRACE_DAYS * DAY_MS;
    const graceEndsAt = new Date(ends).toISOString();
    return now.getTime() < ends ? { level: "grace", reason: "past_due", canGrow: true, graceEndsAt } : { level: "locked", reason: "grace_over", canGrow: false, graceEndsAt };
  }
  if (b && isEndedStatus(b.status)) return { level: "locked", reason: b.status as "canceled" | "unpaid" | "incomplete_expired", canGrow: false };
  // No plan yet (no billing, or Checkout's first payment still incomplete): setup, and each record waits for the card.
  const reason: PlanGateReason = b?.status === "incomplete" ? "incomplete" : "no_plan";
  // Not open in its county yet (docs/WAITLIST.md): no card can be added, so holding a record would be a dead end. Setup and every
  // free feature stay open and records save; inviting still waits for the plan, and the plan waits for the county.
  if (o.held) return { level: "setup", reason: "county_not_open", canGrow: false };
  if (org.kind !== "home") return { level: "setup", reason, canGrow: false, holdsRecords: true };
  // A home keeps its own rule: the first record opens FIRST_VALUE_HOURS, then the card.
  if (!org.firstValueAt) return { level: "setup", reason, canGrow: false };
  const ends = Date.parse(org.firstValueAt) + FIRST_VALUE_HOURS * 3_600_000;
  const firstValueEndsAt = new Date(ends).toISOString();
  return now.getTime() < ends ? { level: "setup", reason: "first_value", canGrow: false, firstValueEndsAt } : { level: "locked", reason, canGrow: false, firstValueEndsAt };
}

/** The words, one place (server errors, the web lock screen, the phone's). `ownerName`: who staff ask. */
export const PLAN_WORDS = {
  lockedTitle: "Your plan isn't active",
  growTitle: `Start your ${TRIAL_DAYS} free days to invite your team`,
  growBody: `Inviting people, a second location and bulk work start with your plan. Add a card on Stripe's page; you pay nothing for ${TRIAL_DAYS} days, and you can cancel before.`,
  /** Photos and AI (owner, 2026-10-12): a photo read, a snap, a room photo, a translation start with the plan. The typed way never waits. */
  photoTitle: `Start your ${TRIAL_DAYS} free days to use photos`,
  photoBody: `Photos and AI reading start with your plan. Add a card on Stripe's page; you pay nothing for ${TRIAL_DAYS} days, and you can cancel before. You can still type it in.`,
  photoTyped: "You can still type it in.",
  keeps: "Health check-ins, sick reports and problem reports still work. Your records stay yours: you can read them, download your binder and export everything.",
  /** What a home keeps when its plan isn't running (never held hostage; services/api paywall.ts HOME_KEEPS). */
  homeKeeps: "Your home's lists and records stay yours: you can still read them.",
  fixCard: "Fix your card",
  startPlan: "Start your plan",
  restart: "Restart your plan",
  /** The start page's title when the plan starts again (no second free period: it is charged today). */
  restartTitle: "Start your plan again",
  askOwner: (owner: string | null) => `Ask ${owner ?? "the owner"} to restart the plan.`,
  /** Before the plan ever ran (inviting): start, not restart. */
  askOwnerStart: (owner: string | null) => `Ask ${owner ?? "the owner"} to start the plan.`,
};

/** The one-line reason a locked business (or home: `kind` "home") sees (owner or admin; staff get `PLAN_WORDS.askOwner`). */
export function lockedLine(a: PlanAccess, kind?: string | null): string {
  switch (a.reason) {
    case "grace_over": return "Your last payment didn't go through, and the 7 days to fix it are over. Fix your card to open everything again.";
    case "unpaid": return "Your payments didn't go through. Fix your card to open everything again.";
    case "canceled": case "incomplete_expired": return "Your plan ended. Start it again to open everything.";
    default: return `Start your ${trialDaysFor(kind)} free days to keep going. ${kind === "home" ? homeOfferLine() : `You pay nothing for ${TRIAL_DAYS} days, and you can cancel before.`}`;
  }
}

/** The owner's one button on the lock screen: the Portal to fix a card, else Checkout to start (or restart) the plan. */
export function lockedAction(a: PlanAccess): "portal" | "checkout" {
  return a.reason === "grace_over" || a.reason === "unpaid" || a.reason === "past_due" ? "portal" : "checkout";
}

/** The grace banner: "Your payment didn't go through. Fix your card by <day> to keep everything open." */
export function graceLine(graceEndsOn: string): string {
  return `Your last payment didn't go through. Fix your card by ${graceEndsOn} to keep everything open.`;
}

/* ─────────────── the card at the moment of saving (owner, 2026-10-10) ─────────────── */

/**
 * Never wall safety: what always saves before a card (owner's standing rule; coordinator decision 2026-10-10, F2).
 * - Records by template: the health check-in, the cut-or-burn record a problem report writes, a recall response (pulling a
 *   recalled food) and the fire safety check.
 * - Routes, never held (services/api): health check-ins (also on the shop tablet), sick reports, incident reports and their
 *   resolve (paywall.ts ALWAYS_RULES), and cooling batches with their readings (routes/cooling.ts: the 2- and 6-hour reminders
 *   protect the food, and holding the batch would silence them).
 */
export const ALWAYS_SAVES_TEMPLATES: readonly string[] = ["health_checkin", "injury_response", "recall_response", "fire_check"];
/** The record routes that never wait for the card, by name (documented here; the server simply never calls holdForCard there). */
export const ALWAYS_SAVES_ROUTES: readonly string[] = ["checkins", "kiosk/checkin", "incidents", "incidents/:id/confirm", "incidents/:id", "health-decisions", "cooling", "cooling/:id/reading", "cooling/:id/fix"];
/** How many entries one person may have waiting for the card in one business (then the card first). */
export const MAX_HELD_ENTRIES = 25;

/** Is this save held for the card? A business with no card yet, and not a record that always saves. */
export function recordHeld(a: Pick<PlanAccess, "holdsRecords">, templateId?: string): boolean {
  return a.holdsRecords === true && !(templateId && ALWAYS_SAVES_TEMPLATES.includes(templateId));
}

/** "1 entry" / "3 entries". */
export const entriesWord = (n: number) => (n === 1 ? "1 entry" : `${n} entries`);

/** The words, one place (server, web, phone). The phone never names a price or a card button (App Store 3.1.3). */
export const CARD_TO_SAVE = {
  title: "Almost there! 🎉",
  body: "Add your card to save this to your log.",
  addCard: "Add a card",
  notNow: "Not now",
  /** Staff (no card rights): who adds it. */
  askOwner: (owner: string | null) => `Ask ${owner ?? "the owner"} to add the card to save this to your log. We kept it for you.`,
  /**
   * The phone, where a link to the web plan may show (a United States storefront build with EXPO_PUBLIC_IOS_PURCHASE_LINK=on;
   * App Store Review Guidelines 3.1.1(a), 3.1.3): the card is added on the web. Never a price or a buy button on the phone.
   */
  phone: "Add your card on the web to save this to your log.",
  /**
   * The phone on any other store build (every Android build; iOS without the link). Guideline 3.1.3(f), read 2026-10-10: a free
   * companion app may have "no purchasing inside the app, or calls to action for purchase outside of the app"; Google Play may
   * not "lead users to a payment method other than Google Play's billing". So plain facts: kept, saves once the plan runs, and
   * where the plan lives. No "add your card", no link, no price.
   */
  phoneStore: "We kept it. It saves to your log once your plan is running. Your plan is managed on the web.",
  /** On the ask, next to the price line: when the free days start. */
  freeDays: `Your ${TRIAL_DAYS} free days start when you add it.`,
  /** On the ask, under Not now: nothing is lost. */
  notNowKeeps: "Not now keeps it here for you, waiting for your card.",
  /** What "kept" means, said once on the ask and on the waiting line. */
  kept: "We kept it for you. It isn't in your log, your binder or Today's done until your card is added, and an inspector never sees it.",
  /** How long it waits. */
  expiry: "It waits for your card as long as your business is here. If the business is deleted, it's deleted with it.",
  /** The form closes with this when its Save was kept for the card. */
  keptToast: (what: string) => `Kept for your card: ${what}. It saves to your log when your card is added.`,
  waitingLine: (n: number) => `${entriesWord(n)} waiting for your card`,
  savedLine: (n: number) => `Saved to your log: ${entriesWord(n)}.`,
  /** The 402's message when the person has too many waiting. */
  full: (n: number) => `You have ${entriesWord(n)} waiting for your card. Add your card to save them, then keep going.`,
};

/**
 * What the person has already made here, in their own records' numbers (CONVERSION-2026-10-13.md C6, C7): said where the plan is
 * decided (the trial's last days on Today, /billing/start, the lock screen), so the choice is about something real. Never a threat:
 * records stay readable, printable and exportable whatever happens (the "never held hostage" rule above), and the words say so.
 */
export const VALUE_WORDS = {
  /** The trial's last days, under "Your free trial ends in N days" (n > 0; `since` is the first free day, already pretty). */
  savedSince: (n: number, since: string) => `${entriesWord(n)} saved since ${since}. They stay yours either way.`,
  /** /billing/start when entries wait for the card (n > 0). */
  waitingLede: (n: number) => `${entriesWord(n)} ${n === 1 ? "is" : "are"} waiting for your card. Add it and ${n === 1 ? "it saves" : "they save"} to your log, with the time each was entered.`,
  /** The lock screen's "Still yours." line, before the usual words (n > 0). */
  stillYours: (n: number) => `${entriesWord(n)} saved here.`,
} as const;
